import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import { GetTokenExpirationTime, RefreshToken } from "graphql/admin/users";
import { isLogin } from "utils/auth";
import { differenceInSeconds } from "date-fns";

const EXPIRATION_NOTIFICATION_THRESHOLD = 60 * 10; // Notify 10 minutes before token expires.
const EXPIRATION_AUTO_REFRESH_THRESHOLD = 60 * 5; // Refresh token if active in the past 5 minutes.

export const useTokenExpiration = (): any => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isTokenExpiring, setIsTokenExpiring] = useState(false);
  const [hasTokenExpired, setHasTokenExpired] = useState(false);

  const { data } = useQuery(GetTokenExpirationTime, {
    variables: {
      token
    },

    skip: !isLogin()
  });

  const expirationTime = data?.tokenExpirationTime;

  const [refreshToken] = useMutation(RefreshToken);

  async function handleTokenRefresh(): Promise<void> {
    const response = await refreshToken({ variables: { token } });

    const newToken = response.data?.refreshToken;

    localStorage.setItem("token", newToken);
    setToken(newToken);

    setIsTokenExpiring(false);
    setHasTokenExpired(false);
  }

  useEffect(() => {
    if (isLogin()) {
      const handle = setInterval(() => {
        const timeRemaining = expirationTime - Math.floor(Date.now() / 1000);

        if (timeRemaining <= EXPIRATION_NOTIFICATION_THRESHOLD) {
          const lastActivity = localStorage.getItem("lastActivity");
          if (
            lastActivity &&
            differenceInSeconds(new Date(), new Date(lastActivity)) <=
              EXPIRATION_AUTO_REFRESH_THRESHOLD
          ) {
            handleTokenRefresh();
          } else {
            setIsTokenExpiring(true);
          }
        }

        if (timeRemaining <= 0) {
          setHasTokenExpired(true);
        }
      }, 1000 * 30); // Check every 30 seconds.

      return () => clearInterval(handle);
    }
  }, [expirationTime]);

  return {
    expirationTime,
    isTokenExpiring,
    hasTokenExpired,
    handleTokenRefresh
  };
};
